// src/components/EvidenceManager.js - Updated version with index integration
import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../firebase/AuthContext';
import { doc, updateDoc, serverTimestamp, collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { FileDown } from 'lucide-react';
import Fuse from 'fuse.js';

// Import components
import PdfTable from './pdf-table/PdfTable';
import PdfViewer from './pdf-viewer/PdfViewer';
import ErrorBoundary from './ErrorBoundary';
import SearchInput from './ui/SearchInput';
import IconButton from './ui/IconButton';
import ScrollContainer from './ui/ScrollContainer';
import ViewerEmptyState from './pdf-viewer/ViewerEmptyState';
import PdfSkeleton from './pdf-viewer/PdfSkeleton';
import DownloadCompleteModal from './ui/DownloadCompleteModal';
import useGuide, { incrementActionCount } from '../hooks/useGuide';
import BulkTagGuide from './ui/BulkTagGuide';

// Import services
import CloudFileService from '../services/CloudFileService';
import { exportCombinedPdf, exportSinglePdf } from '../services/PdfExportService';
import EvidenceDataService from '../services/EvidenceDataService';
import { IndexService } from './index/IndexGenerator';
import { percentagesToPdfPoints, pdfPointsToPercentages, getDefaultStampLocationPercent } from '../utils/PdfPositionUtils';
import { isPaymentEnforcementEnabled } from '../utils/RemoteConfig';
import SubscriptionService from '../services/SubscriptionService';
import DocumentSetService from '../services/DocumentSetService';

import {
  trackFileUploadStarted,
  trackFileUploadCompleted,
  trackFileUploadFailed,
  trackFileReordered,
  trackFileRenamed,
  trackFileDeleted,
  trackStampPositionAdjusted,
  trackExportStarted,
  trackExportCompleted,
  trackExportFailed,
  trackFilePreviewOpened,
  trackPerformance,
  trackBulkSetsAddedToDocuments,
  trackBulkSetsRemovedFromDocuments
} from '../utils/analytics';

/**
 * Get processing status for optimized single-write workflow
 * Documents are NOT viewable until server completes ALL processing
 */
const getProcessingStatus = (file) => {
  const status = file.status;
  
  switch (status) {
    case 'processing':
      return {
        text: 'Processing...',
        color: 'blue',
        canView: false,
        description: 'File is being converted and made searchable'
      };
      
    case 'completed':
      // Only completed when EVERYTHING is done (conversion + OCR + stamp calculation)
      return {
        text: 'Ready',
        color: 'green',
        canView: true,
        description: 'Document is fully processed and ready'
      };
      
    case 'client_error':
      return {
        text: 'Error',
        color: 'red',
        canView: false,
        description: 'File could not be processed client-side'
      };

    case 'error':
    case 'ocr_failed':
      return {
        text: 'Error',
        color: 'red',
        canView: false,
        description: file.conversionError || file.error || 'Processing failed'
      };
      
    default:
      return {
        text: 'Unknown',
        color: 'gray',
        canView: false,
        description: 'Status unknown'
      };
  }
};

/**
 * Convert a raw server error string into a user-facing message.
 * Errors prefixed with FILE_* are already user-friendly and stored verbatim.
 * Everything else gets a generic fallback.
 */
const getUserFacingErrorMessage = (error) => {
  if (!error) return null;

  const userFacingPrefixes = [
    'FILE_ENCRYPTED:',
    'FILE_TOO_LARGE:',
    'FILE_TYPE_MISMATCH:',
    'FILE_CORRUPTED:',
  ];

  for (const prefix of userFacingPrefixes) {
    if (error.startsWith(prefix)) {
      return error.replace(prefix, '').trim();
    }
  }

  return 'This document failed to process. Please delete it from the document table and upload it again. If the issue persists, you can report a bug to support@stampstack.io.';
};

/**
 * Check if file is ready to view
 * File must have: searchablePdfUrl, stampLocation, pdfDimensions
 */
const isFileReadyToView = (file) => {
  return file.status === 'completed' &&
         file.searchablePdfUrl &&
         file.stampLocation &&
         file.pdfDimensions;
};

/**
 * Get viewing capability for file
 */
const getViewingCapability = (file) => {
  // Check if file has everything needed to be viewable
  const status = getProcessingStatus(file);
  const hasAllData = isFileReadyToView(file);
  
  return {
    canView: status.canView && hasAllData,
    reason: !status.canView ? status.description : 
            !hasAllData ? 'Missing required data' : 
            'Ready to view'
  };
};

/**
 * Evidence Manager Component - Optimized workflow version
 */
function EvidenceManager({
  caseId,
  stampConfig,
  onStampConfigChange,
  caseData,
  onCaseModified,
  onExportStateChange, // Callback to update export state in CaseManager
  exportTriggerRef,     // Ref to connect header button to export function
  selectedSetId = null,
  // Document sets - SINGLE SOURCE OF TRUTH from CaseManager
  documentSets = [],
  onDocumentSetsChange, // Callback to update sets in CaseManager
  // UI preferences
  isSetsCollapsed = false,
  onSetsCollapsedChange,
  // Filter to error rows only
  filterErrors = false,
  // Archive handler
  onArchiveCase,
  onStartTagGuide,
}) {

  console.log('🔧 EvidenceManager - Props received:', {
    caseId,
    hasStampConfig: !!stampConfig,
    hasCaseData: !!caseData,
    hasExportTriggerRef: !!exportTriggerRef,
    hasOnExportStateChange: !!onExportStateChange,
    documentSetsCount: documentSets.length,
    hasOnDocumentSetsChange: !!onDocumentSetsChange
  });

  // Get auth context
  const { currentUser } = useAuth();

  // State management
  const [pdfFiles, setPdfFiles] = useState([]);
  const [selectedPdfId, setSelectedPdfId] = useState(null);
  const [status, setStatus] = useState('Upload one or more files to get started.');
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [processingSummary, setProcessingSummary] = useState({
    total: 0,
    processing: 0,
    ready: 0,
    failed: 0,
    canExport: false
  });
  
  const [bulkTagStep, setBulkTagStep] = useState(1);
  const [selectedDocCount, setSelectedDocCount] = useState(0);
  const [forceBulkGuide, setForceBulkGuide] = useState(false);

  const {
    visible: bulkTagVisible,
    hardDismiss: bulkTagHardDismiss,
  } = useGuide('bulkTagGuide', { localCondition: selectedDocCount > 0 });

  // Reset to page 1 when guide is hidden
  useEffect(() => {
    if (!bulkTagVisible && !forceBulkGuide) setBulkTagStep(1);
  }, [bulkTagVisible, forceBulkGuide]);

  // Wipe guide state on case switch so neither path can reopen on switch-back
  useEffect(() => {
    setForceBulkGuide(false);
    setSelectedDocCount(0);
  }, [caseId]);

  const handleBulkTagGotIt = useCallback(async () => {
    setForceBulkGuide(false);
    bulkTagHardDismiss();
    if (currentUser?.uid) {
      await incrementActionCount(currentUser.uid, 'bulkTagGuideInteracted');
    }
  }, [bulkTagHardDismiss, currentUser?.uid]);

  const handleBulkTagClose = useCallback(async () => {
    setForceBulkGuide(false);
    bulkTagHardDismiss();
    if (currentUser?.uid) {
      await incrementActionCount(currentUser.uid, 'bulkTagGuideInteracted');
    }
  }, [bulkTagHardDismiss, currentUser?.uid]);

  const handleNextStep = useCallback(() => {
    setBulkTagStep(prev => Math.min(prev + 1, 3));
  }, []);

  const handleStepChange = useCallback((newStep) => {
    setBulkTagStep(newStep);
  }, []);

  const handleStartBulkGuide = useCallback(() => {
    setBulkTagStep(1);
    setForceBulkGuide(true);
  }, []);


  // Download complete modal state
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [downloadModalData, setDownloadModalData] = useState({
    caseName: '',
    documentSet: null,
    allDocumentIds: []
  });

  // Index document state
  const [indexDocument, setIndexDocument] = useState(null);

  const [stampPositions, setStampPositions] = useState(new Map()); // fileId -> {x, y} percentages

  // Search functionality state
  const [searchTerm, setSearchTerm] = useState(''); // Immediate input value
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Track first search per session (empty → non-empty transition)
  const prevSearchTermRef = useRef('');
  useEffect(() => {
    if (debouncedSearchTerm && !prevSearchTermRef.current && currentUser?.uid) {
      incrementActionCount(currentUser.uid, 'searchForDoc');
    }
    prevSearchTermRef.current = debouncedSearchTerm;
  }, [debouncedSearchTerm, currentUser?.uid]);

  const currentIndexUrlRef = useRef(null);

  // Track when files complete OCR/conversion processing
  const prevFileStatusesRef = useRef(new Map());
  useEffect(() => {
    if (!currentUser?.uid) return;
    pdfFiles.forEach(file => {
      const prevStatus = prevFileStatusesRef.current.get(file.id);
      if (file.status === 'completed' && prevStatus !== undefined && prevStatus !== 'completed') {
        incrementActionCount(currentUser.uid, 'docMadeSearchable');
        if (file.fileType && file.fileType !== 'application/pdf') {
          incrementActionCount(currentUser.uid, 'docConvertedToPdf');
        }
      }
    });
    const newMap = new Map();
    pdfFiles.forEach(file => newMap.set(file.id, file.status));
    prevFileStatusesRef.current = newMap;
  }, [pdfFiles, currentUser?.uid]);

  // Debouncing state for real-time listener to prevent thundering herd
  const pendingUpdatesRef = useRef({ updates: [], removals: [] });
  const debounceTimerRef = useRef(null);

  // Create a key that changes when files are added/removed/reordered OR when pageCount changes
  const fileDataKey = useMemo(() => {
    const fileData = pdfFiles.map(f => `${f.id}:${f.pageCount || 0}:${f.name || ''}`).join(',');
    const stampData = `${stampConfig?.partyType || ''}:${stampConfig?.startingValue || ''}:${stampConfig?.exhibitType || ''}`;
    return `${fileData}|${stampData}`;
  }, [pdfFiles, stampConfig?.partyType, stampConfig?.startingValue, stampConfig?.exhibitType]);

  // Create placeholder index document (no PDF blob)
  useEffect(() => {
    if (pdfFiles.length > 0 && stampConfig && caseData) {
      // Only create placeholder if it doesn't exist yet
      // This prevents overwriting an existing index with a URL
      setIndexDocument(prev => {
        // If we already have an index, keep it (preserve the URL if it exists)
        if (prev) {
          return prev;
        }
        
        // Create new placeholder only if none exists
        return {
          id: 'INDEX_DOCUMENT',
          name: "Index",
          isIndex: true,
          pageCount: 1,
          status: 'completed',
          showSpinner: false,
          searchablePdfUrl: null, // No URL yet
          url: null,
        };
      });
      
      console.log('📋 Placeholder index document created');
    } else {
      setIndexDocument(null);
    }
  }, [pdfFiles.length, stampConfig, caseData]);

  // Generate index PDF on-demand when needed
  const generateIndexPdfUrl = useCallback(async () => {
    if (!pdfFiles.length || !stampConfig || !caseData) {
      console.warn('Cannot generate index: missing required data');
      return null;
    }

    try {
      console.log('📋 Generating index PDF on-demand...');
      
      // Store the old URL to revoke it AFTER the new one is set
      const oldUrl = currentIndexUrlRef.current;
      
      // NEW: Determine what documents to include in the index
      let documentsForIndex;
      let documentSetInfo = null;
      
      if (selectedSetId) {
        // Viewing a document set - filter documents
        const setDocuments = pdfFiles.filter(file => {
          if (file.isIndex) return false;
          return (file.documentSets || []).includes(selectedSetId);
        });
        
        // Get the set name for the index
        const selectedSet = documentSets.find(s => s.id === selectedSetId);
        documentSetInfo = selectedSet ? {
          name: selectedSet.name,
          color: selectedSet.color
        } : null;
        
        // Use inherited numbers for filtered set
        documentsForIndex = EvidenceDataService.getFilteredDisplayDataWithInheritedNumbers(
          pdfFiles,      // Full array for inherited numbers
          setDocuments,  // Filtered set for index
          stampConfig,
          caseData
        );
        
        console.log(`📋 Generating index for document set "${selectedSet?.name}" with ${documentsForIndex.length} documents`);
      } else {
        // FIXED: Viewing full case - calculate display data (not raw pdfFiles)
        documentsForIndex = EvidenceDataService.getDocumentDisplayData(
          pdfFiles, 
          stampConfig, 
          caseData
        );
        console.log(`📋 Generating index for full case with ${documentsForIndex.length} documents`);
      }
      
      // Generate new URL with appropriate documents
      const indexUrl = await IndexService.generateIndexURL(
        documentsForIndex, 
        stampConfig, 
        caseData, 
        documentSetInfo
      );
      currentIndexUrlRef.current = indexUrl;
      
      console.log('✅ Index PDF generated on-demand');
      
      // Revoke the old URL AFTER a delay to ensure viewer has switched to new URL
      if (oldUrl) {
        setTimeout(() => {
          console.log('🧹 Revoking old index URL after safe delay');
          URL.revokeObjectURL(oldUrl);
        }, 1000);
      }
      
      return indexUrl;
      
    } catch (error) {
      console.error('Error generating index PDF:', error);
      return null;
    }
  }, [pdfFiles, stampConfig, caseData, selectedSetId, documentSets]);

  const handleSelectPdf = useCallback(async (fileId) => {
    console.log('📄 Selecting PDF:', fileId);
    
    // If selecting the index, generate it on-demand
    if (fileId === 'INDEX_DOCUMENT') {
      console.log('📋 Index selected - generating PDF...');
      
      const indexUrl = await generateIndexPdfUrl();
      
      if (indexUrl) {
        setIndexDocument(prev => prev ? {
          ...prev,
          searchablePdfUrl: indexUrl,
          url: indexUrl
        } : null);
      }
    }
    
    setSelectedPdfId(fileId);
  }, [generateIndexPdfUrl]);

  // Regenerate index when MEANINGFUL file data changes IF currently viewing it
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Only regenerate if:
    // 1. Index is currently selected
    // 2. We have files to generate from
    // 3. Index has been generated at least once (has a URL)
    if (selectedPdfId === 'INDEX_DOCUMENT' && pdfFiles.length > 0 && indexDocument?.searchablePdfUrl) {
      console.log('📋 Files changed while viewing index - regenerating...');
      
      const regenerateIndex = async () => {
        const indexUrl = await generateIndexPdfUrl();
        
        if (indexUrl) {
          setIndexDocument(prev => prev ? {
            ...prev,
            searchablePdfUrl: indexUrl,
            url: indexUrl
          } : null);
        }
      };
      
      // Debounce to prevent rapid regeneration
      const timeoutId = setTimeout(regenerateIndex, 100);
      return () => clearTimeout(timeoutId);
    }
  // fileDataKey only changes when ID, pageCount, or name changes
  }, [fileDataKey, selectedSetId]);

  // Cleanup index document on unmount
  useEffect(() => {
    return () => {
      if (currentIndexUrlRef.current) {
        URL.revokeObjectURL(currentIndexUrlRef.current);
        currentIndexUrlRef.current = null;
      }
    };
  }, []);

  // Create combined file list for display (index + documents)
  const displayFiles = useMemo(() => {
    const files = [];
    const seenIds = new Set();
    
    // Add index document first if available
    if (indexDocument && !seenIds.has(indexDocument.id)) {
      files.push(indexDocument);
      seenIds.add(indexDocument.id);
    }
    
    // Add all regular documents with processing status
    pdfFiles.forEach(file => {
      if (seenIds.has(file.id)) {
        console.warn('🔧 Prevented duplicate file in displayFiles:', file.id, file.name);
        return;
      }
      
      // Show spinner only for actively processing files, not terminal error states
      const isProcessing =
        file.status === 'processing' ||
        (file.status === 'completed' && !file.searchablePdfUrl);
      
      files.push({
        ...file,
        showSpinner: isProcessing,
      });
      
      seenIds.add(file.id);
    });
    
    console.log('📋 DisplayFiles created:', {
      totalFiles: files.length,
      indexIncluded: !!indexDocument,
      regularFiles: pdfFiles.length,
      processingFiles: files.filter(f => f.showSpinner).length,
      fileIds: files.map(f => f.id)
    });
    
    return files;
  }, [indexDocument, pdfFiles]);

  // Filter files by selected set
  const filteredBySet = useMemo(() => {
    if (!selectedSetId) {
      return displayFiles; // No filter - show all
    }

    // Filter to only files that have this set
    const filtered = displayFiles.filter(file => {
      // Skip index for now
      if (file.isIndex) return false;

      // Check if file has the selected set
      const documentSets = file.documentSets || [];
      return documentSets.includes(selectedSetId);
    });

    // Only include index if there are matching documents
    if (filtered.length > 0) {
      const indexDoc = displayFiles.find(f => f.isIndex);
      if (indexDoc) {
        return [indexDoc, ...filtered];
      }
    }

    return filtered;
  }, [displayFiles, selectedSetId]);

  // Configure Fuse.js for fuzzy search on PDF files
  const fuse = useMemo(() => {
    const options = {
      keys: [
        { name: 'name', weight: 0.7 },
      ],
      threshold: 0.3, // Lower = more strict matching
      ignoreLocation: true,
      includeScore: true
    };
    
    return new Fuse(filteredBySet, options);
  }, [filteredBySet]);

  // Filter files based on search term
  const filteredFiles = useMemo(() => {
    if (!debouncedSearchTerm.trim()) {
      return filteredBySet;
    }

    // When searching, exclude the index document from the search results
    const searchResults = fuse.search(debouncedSearchTerm);
    return searchResults
      .map(result => result.item)
      .filter(file => !file.isIndex);
  }, [filteredBySet, debouncedSearchTerm, fuse]);

  // Determine if we're in a filtered context
  const isFiltered = useMemo(() => {
    return !!selectedSetId || !!debouncedSearchTerm.trim();
  }, [selectedSetId, debouncedSearchTerm]);

  // Calculate display data with appropriate method
  // PERFORMANCE: Add stable key to prevent recalculation when file references change
  // but actual data hasn't changed
  const filesKey = useMemo(() => {
    return filteredFiles.map(f => `${f.id}:${f.pageCount}:${f.status}`).join('|');
  }, [filteredFiles]);

  const documentsWithDisplayData = useMemo(() => {
    if (!filteredFiles || filteredFiles.length === 0) return [];

    if (isFiltered) {
      // When filtering (by set or search), maintain exhibit numbers from full array
      return EvidenceDataService.getFilteredDisplayDataWithInheritedNumbers(
        displayFiles,   // Full unfiltered array
        filteredFiles,  // Filtered subset
        stampConfig,
        caseData
      );
    } else {
      // Normal calculation for main case view
      return EvidenceDataService.getDocumentDisplayData(
        filteredFiles,
        stampConfig,
        caseData
      );
    }
  }, [filesKey, displayFiles, isFiltered, stampConfig, caseData]);

  // Helper to get selected file with enhanced display data
  const selectedFile = useMemo(() => {
    // Find the selected file from all files (index + regular)
    const file = displayFiles.find(file => file.id === selectedPdfId);
    if (!file) return null;

    // For index documents, return with proper file structure
    if (file.isIndex) {
      return {
        ...file,
        file: {
          url: file.searchablePdfUrl,
          name: file.name
        }
      };
    }

    // For regular documents, add exhibit info AND startPage
    const documentIndex = pdfFiles.findIndex(f => f.id === selectedPdfId);
    const stampData = EvidenceDataService.getStampData(documentIndex, stampConfig, caseData);
    
    // Calculate startPage using the single source of truth.
    // IMPORTANT: read startPage from documentsWithDisplayData (the filter-aware
    // memo), NOT from the full pdfFiles array. The Bates/page number must restart
    // at 1 within the current filtered set so it stays consistent with the index
    // and the exported PDF. Exhibit numbers are still inherited from the full
    // array (handled inside documentsWithDisplayData), so cross-referencing holds.
    const thisDocDisplayData = documentsWithDisplayData.find(d => d.id === selectedPdfId);
    const startPage = thisDocDisplayData?.startPage || 1;

    return {
      ...file,
      documentIndex,
      exhibitNumber: stampData.exhibitNumber,
      displayCaseNumber: stampData.caseNumber,
      startPage, // single source of truth for page numbering (filter-aware)
      // Include file structure for PdfViewer - prefer searchable PDF
      file: {
        url: file.searchablePdfUrl,
        name: file.name
      }
    };
  }, [displayFiles, selectedPdfId, pdfFiles, stampConfig, caseData, documentsWithDisplayData]);
  
  // Processing summary calculation - Enhanced for optimized workflow
  const calculatedProcessingSummary = useMemo(() => {
    // Get the files to check based on current filter state
    // When filtering by SET, use filteredBySet (set filter only, excludes index)
    // When NO set is selected, use all pdfFiles (ignore search filter for export button)
    const filesToCheck = selectedSetId
      ? filteredBySet.filter(f => !f.isIndex)
      : pdfFiles;

    if (filesToCheck.length === 0) {
      return {
        total: 0,
        processing: 0,
        ready: 0,
        failed: 0,
        canExport: false
      };
    }

    let processing = 0;
    let ready = 0;
    let failed = 0;

    filesToCheck.forEach(file => {
      const capability = getViewingCapability(file);
      const status = getProcessingStatus(file);

      if (status.text === 'Processing...') {
        processing++;
      } else if (status.text === 'Error') {
        failed++;
      } else if (capability.canView) {
        ready++;
      }
    });

    const canExport = processing === 0 && failed === 0 && ready === filesToCheck.length;

    // Scope-aware: only counts errors within the current export scope (the selected tag group,
    // or the whole case when no tag is selected — same `filesToCheck` that drives canExport).
    // This lets a clean tag group export even when other docs in the case have errors, and it
    // intentionally ignores the text-search filter (filesToCheck is not search-filtered).
    const hasErrors = filesToCheck.some(d =>
      d.status === 'client_error' || d.status === 'error' || d.status === 'ocr_failed'
    );

    return {
      total: filesToCheck.length,
      processing,
      ready,
      failed,
      canExport,
      hasErrors,
    };
  }, [pdfFiles, filteredBySet, selectedSetId]);

  // Update processing summary when it changes
  useEffect(() => {
    setProcessingSummary(calculatedProcessingSummary);
  }, [calculatedProcessingSummary]);

  // Status message helper
  const showStatus = useCallback((message, error = null) => {
    console.log('EvidenceManager - Status:', message, error);
    setStatus(message);
    if (error) {
      console.error('EvidenceManager - Error details:', error);
    }
  }, []);

  /**
   * Debounced handler for real-time file updates
   * Batches rapid updates to prevent "thundering herd" of concurrent requests
   * Waits 500ms before processing to allow multiple updates to batch together
   */
  const processPendingUpdates = useCallback(() => {
    const { updates, removals } = pendingUpdatesRef.current;
    
    if (updates.length === 0 && removals.length === 0) return;

    console.log(`🔄 Processing ${updates.length} updates and ${removals.length} removals`);
    pendingUpdatesRef.current = { updates: [], removals: [] };

    // Process file updates directly
    setPdfFiles(currentFiles => {
      // Create a map of current files by ID for fast lookup
      const fileMap = new Map(currentFiles.map(file => [file.id, file]));

      let hasChanges = false;

      // Handle removals first
      removals.forEach(fileId => {
        if (fileMap.has(fileId)) {
          console.log('🗑️ Removed file:', fileId);
          fileMap.delete(fileId);
          hasChanges = true;
        }
      });

      // Process each update
      updates.forEach(update => {
        // IMPROVED: More robust name fallback chain
        const getName = () => {
          if (update.name) return update.name;
          if (update.displayName) return update.displayName;
          if (update.originalName) {
            // Remove file extension
            return update.originalName.replace(/\.[^.]+$/i, '');
          }

          // Check if existing file has a name
          const existingFile = fileMap.get(update.id);
          if (existingFile?.name) return existingFile.name;

          // Last resort
          console.warn('⚠️ Update missing name field:', update.id, update);
          return 'Unnamed Document';
        };

        // Ensure name field is always set
        const fixedUpdate = {
          ...update,
          name: getName(),
        };

        const existingFile = fileMap.get(update.id);

        if (existingFile) {
          // File exists - check if there are actual changes
          const hasActualChanges = Object.keys(fixedUpdate).some(key => {
            // Deep comparison for objects, shallow for primitives
            if (typeof fixedUpdate[key] === 'object' && fixedUpdate[key] !== null) {
              return JSON.stringify(existingFile[key]) !== JSON.stringify(fixedUpdate[key]);
            }
            return existingFile[key] !== fixedUpdate[key];
          });

          if (hasActualChanges) {
            // console.log('✅ Updated file', fixedUpdate.name || fixedUpdate.id);
            fileMap.set(update.id, { ...existingFile, ...fixedUpdate });
            hasChanges = true;
          }
        } else {
          // New file - add it
          // console.log('➕ Added new file:', fixedUpdate.name || fixedUpdate.id);
          fileMap.set(update.id, fixedUpdate);
          hasChanges = true;
        }
      });

      // Only return new array if there were changes
      if (hasChanges) {
        console.log('🔧 EvidenceManager - Applied real-time updates');
        const updatedFiles = Array.from(fileMap.values());
        // Sort by order
        updatedFiles.sort((a, b) => (a.order || 0) - (b.order || 0));
        return updatedFiles;
      }

      return currentFiles; // No changes, return same reference to prevent re-render
    });
  }, []);

  const queueRealtimeUpdate = useCallback((fileUpdates, fileRemovals = []) => {
    // Merge with existing pending changes
    const currentUpdates = new Map(
      pendingUpdatesRef.current.updates.map(u => [u.id, u])
    );
    fileUpdates.forEach(u => currentUpdates.set(u.id, u));
    
    const currentRemovals = new Set(pendingUpdatesRef.current.removals);
    fileRemovals.forEach(id => currentRemovals.add(id));
    
    // If a file is being removed, don't also update it
    currentRemovals.forEach(id => currentUpdates.delete(id));
    
    pendingUpdatesRef.current = {
      updates: Array.from(currentUpdates.values()),
      removals: Array.from(currentRemovals)
    };

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounce timer - reduced to 100ms for faster feedback
    debounceTimerRef.current = setTimeout(() => {
      processPendingUpdates();
    }, 100);

    console.log(`⏱️ Queued ${fileUpdates.length} updates, ${fileRemovals.length} removals`);
  }, [processPendingUpdates]);

  // NEW: Helper function to get current stamp position for a file
  const getCurrentStampPosition = useCallback((file) => {
    // Skip stamp position logic for INDEX_DOCUMENT
    if (file.id === 'INDEX_DOCUMENT' || file.isIndex) {
      return null;
    }
    
    console.log('🎯 getCurrentStampPosition called for file:', file.id);

    // 1. Check if we have a local position (user has moved it)
    const localPosition = stampPositions.get(file.id);
    // console.log('🗺️ Local position from Map:', localPosition);

    if (localPosition) {
      // console.log('✅ Using local position:', localPosition);
      return localPosition;
    }
    
    // 2. Check if we have a saved position in the file metadata
    if (file.stampLocation && file.pdfDimensions) {
      const dbPosition = pdfPointsToPercentages(
        file.stampLocation,
        file.pdfDimensions.width,
        file.pdfDimensions.height
      );
      console.log('💾 Using database position:', dbPosition);
      return dbPosition;
    }
    
    // 3. Use default position
    const defaultPosition = getDefaultStampLocationPercent();
    console.log('🎯 Using default position:', defaultPosition);
    return defaultPosition;
  }, [stampPositions]);

  // Load files for this case
  const loadFiles = useCallback(async () => {
    if (!currentUser?.uid || !caseId) {
      console.log('EvidenceManager - Cannot load files: missing user or case ID');
      return;
    }

    setIsLoading(true);
    try {
      console.log('EvidenceManager - Loading files for case:', caseId);
      const files = await CloudFileService.getFiles(caseId);
      console.log('EvidenceManager - Loaded files:', files.length);
      setPdfFiles(files);
      showStatus(`Loaded ${files.length} files.`);
    } catch (error) {
      console.error('EvidenceManager - Error loading files:', error);
      showStatus('Error loading files', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser?.uid, caseId, showStatus]);

  // Real-time file updates handler

  // Load files when case changes
  useEffect(() => {
    if (!caseId || !currentUser?.uid) {
      // Clear files if no case selected
      console.log('🧹 Clearing files - no case selected');
      setPdfFiles([]);
      setSelectedPdfId(null);
      setIndexDocument(null);
      setIsLoading(false); // Not loading when no case
      return;
    }

    console.log('🔄 Case changed - loading files for case:', caseId);
    
    // Set loading state FIRST before clearing files
    setIsLoading(true);
    
    // Clear previous case's files immediately
    setPdfFiles([]);
    setSelectedPdfId(null);
    setIndexDocument(null);
    
    // Cleanup: clear files when unmounting or case changes
    return () => {
      console.log('🧹 Cleanup: clearing files for case:', caseId);
    };
  }, [caseId, currentUser?.uid, loadFiles]);

  // Document sets are now passed as props from CaseManager - no separate loading needed!

  // Add files — invalid files get a Firestore error doc, valid files are uploaded
  const addFiles = useCallback(async (newFiles) => {
    if (!currentUser?.uid || !caseId) return;
    if (!newFiles || newFiles.length === 0) return;

    const startTime = Date.now();
    trackFileUploadStarted(newFiles.length, [...new Set(newFiles.map(f => f.type))]);

    const toUpload = [];

    for (const file of newFiles) {
      const check = CloudFileService.checkFile(file);
      if (!check.valid) {
        await CloudFileService.createErrorDoc(caseId, file, check.errorType, check.errorSubtype || null);
        continue;
      }

      if (await CloudFileService.isPdfEncrypted(file)) {
        await CloudFileService.createErrorDoc(caseId, file, 'restricted_pdf', null);
        continue;
      }

      toUpload.push(file);
    }

    if (toUpload.length > 0) {
      trackFileUploadCompleted(toUpload.length, Date.now() - startTime);
      setIsUploading(true);
      try {
        const uploadResults = await CloudFileService.uploadFiles(
          caseId,
          toUpload,
          currentUser.uid,
          (message, progress) => {
            if (typeof progress === 'number') setUploadProgress(progress);
          }
        );
        // Guide action tracking — preserved from the pre-merge upload flow.
        // Counts are based on actually-uploaded files (toUpload), so invalid/encrypted
        // files filtered out above don't inflate the totals.
        if (currentUser?.uid) {
          for (let i = 0; i < uploadResults.length; i++) {
            await incrementActionCount(currentUser.uid, 'uploadDoc');
          }
          if (uploadResults.length > 1) {
            await incrementActionCount(currentUser.uid, 'uploadMultipleDocs');
          }
        }
        if (selectedSetId && uploadResults.length > 0) {
          await Promise.all(
            uploadResults.map(result =>
              DocumentSetService.addDocumentToSet(caseId, result.id, selectedSetId)
                .catch(err => console.error(`Failed to auto-tag ${result.id}:`, err))
            )
          );
          await incrementActionCount(currentUser.uid, 'uploadDocsToTag');
        }
        onCaseModified?.();
      } catch (error) {
        console.error('EvidenceManager - upload error:', error);
        trackFileUploadFailed(toUpload.length, error.message);
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    }
  }, [currentUser?.uid, caseId, selectedSetId, onCaseModified]);

  // Replace a file doc (preserves doc ID / exhibit order on success)
  const onReplaceFile = useCallback(async (docId, newFile) => {
    if (!currentUser?.uid || !caseId) return;
    try {
      await CloudFileService.replaceFile(caseId, docId, newFile, currentUser.uid);
      onCaseModified?.();
    } catch (error) {
      console.error('EvidenceManager - replace error:', error);
    }
  }, [currentUser?.uid, caseId, onCaseModified]);

  // Handle file name updates
  const handleUpdateFileName = useCallback(async (fileId, newName) => {
    if (!currentUser?.uid || !caseId) {
      console.warn('EvidenceManager - Cannot update file name: missing user or case ID');
      return;
    }

    try {
      console.log('EvidenceManager - Updating file name:', fileId, newName);
      
      // Update local state immediately for responsive UI
      setPdfFiles(prevFiles => 
        prevFiles.map(file => 
          file.id === fileId 
            ? { ...file, name: newName } 
            : file
        )
      );

      // Update cloud storage
      await CloudFileService.updateFileName(caseId, fileId, newName);
      trackFileRenamed(caseId);
      console.log('EvidenceManager - File name updated successfully');
      
      // Note: The useEffect in Step 5 will automatically regenerate the index
      // if it's currently being viewed, because pdfFiles changed
      
    } catch (error) {
      console.error('EvidenceManager - Error updating file name:', error);
      showStatus('Error updating file name', error);
      
      // Reload files to restore correct state
      await loadFiles();
    }
  }, [currentUser?.uid, caseId, showStatus, loadFiles]);

  // NEW: Handle stamp position updates without triggering PDF reload
  const handleStampPositionUpdate = useCallback(async (fileId, stampLocationPercent) => {
    // Skip stamp updates for INDEX_DOCUMENT
    if (fileId === 'INDEX_DOCUMENT' || !fileId) {
      console.log('📋 Skipping stamp update for INDEX_DOCUMENT');
      return;
    }
    
    console.log('EvidenceManager - Stamp position updated (percentages):', fileId, stampLocationPercent);
    trackStampPositionAdjusted(caseId, fileId);

    // DEBUG: Log before and after Map update
    console.log('🗺️ stampPositions Map BEFORE update:', Array.from(stampPositions.entries()));
    
    // Update local stamp position state immediately (no file reload!)
    setStampPositions(prev => {
      const newMap = new Map(prev);
      newMap.set(fileId, stampLocationPercent);
      console.log('🗺️ stampPositions Map AFTER update:', Array.from(newMap.entries()));
      return newMap;
    });
  
    // Save to database in background without affecting local state
    const file = pdfFiles.find(f => f.id === fileId);
    if (file?.pdfDimensions) {
      const stampLocationPageUnits = percentagesToPdfPoints(
        stampLocationPercent,
        file.pdfDimensions.width,
        file.pdfDimensions.height
      );
      
      console.log('EvidenceManager - Converting to page units for database:', stampLocationPageUnits);
      
      // Direct database save - no local state update!
      try {
        await CloudFileService.updateFileMetadata(caseId, fileId, { 
          stampLocation: stampLocationPageUnits 
        });
        console.log('EvidenceManager - Stamp position saved to database successfully');
        onCaseModified?.();
      } catch (error) {
        console.error('EvidenceManager - Failed to save stamp position:', error);
        // Could show a non-blocking error message here
      }
    } else {
      console.warn('EvidenceManager - No PDF dimensions available, cannot convert to page units');
    }
  }, [caseId, pdfFiles, stampPositions]);

  /**
   * Handle download of current PDF with stamp
   */
  const handleDownloadPdf = useCallback(async () => {
    if (!selectedFile) {
      console.warn('No file selected for download');
      return;
    }

    try {
      console.log('📥 Starting single PDF download for:', selectedFile.name);
      
      // Show status message
      showStatus('Preparing download...', 'info');
      
      // Use the new exportSinglePdf function
      await exportSinglePdf(selectedFile, stampConfig, caseData);
      
      // Show success message
      showStatus('PDF downloaded successfully!', 'success');
      
    } catch (error) {
      console.error('❌ Download failed:', error);
      showStatus(`Download failed: ${error.message}`, 'error');
    }
  }, [selectedFile, stampConfig, caseData, showStatus]);
  
  // Remove PDF
  const onRemovePdf = useCallback(async (fileId) => {
    if (!currentUser?.uid || !caseId) {
      showStatus('Error: Not logged in or no case selected');
      return;
    }

    // Don't allow removing the index document
    if (fileId === 'INDEX_DOCUMENT') {
      return;
    }

    try {
      console.log('EvidenceManager - Removing file:', fileId);
      
      // Update local state immediately
      setPdfFiles(prevFiles => prevFiles.filter(file => file.id !== fileId));
      
      // Clear selection if this file was selected
      if (selectedPdfId === fileId) {
        setSelectedPdfId(null);
      }
      
      // Remove from cloud
      await CloudFileService.deleteFile(caseId, fileId, currentUser?.uid);
      showStatus('File removed successfully.');
      onCaseModified?.();

      trackFileDeleted(caseId);
      
    } catch (error) {
      console.error('EvidenceManager - Error removing file:', error);
      showStatus('Error removing file', error);
      
      // Reload files to restore correct state
      await loadFiles();
    }
  }, [currentUser?.uid, caseId, selectedPdfId, showStatus, loadFiles]);

  // Handle reordering
  const handleReorderPdfs = useCallback(async (fileOrderUpdates) => {
    if (!currentUser?.uid || !caseId) {
      console.warn('EvidenceManager - Cannot reorder: missing user or case ID');
      return;
    }
    
    try {
      console.log('EvidenceManager - Reordering files...', fileOrderUpdates);
      
      // STEP 1: Update local state immediately for smooth UI
      // Create a map of fileId -> newOrder from fileOrderUpdates
      const orderMap = new Map();
      fileOrderUpdates.forEach(update => {
        orderMap.set(update.fileId, update.order);
      });
      
      // Reorder the current pdfFiles based on the new order
      const reorderedFiles = [...pdfFiles].sort((a, b) => {
        const orderA = orderMap.has(a.id) ? orderMap.get(a.id) : a.order || 999;
        const orderB = orderMap.has(b.id) ? orderMap.get(b.id) : b.order || 999;
        return orderA - orderB;
      });
      
      // Update local state immediately
      setPdfFiles(reorderedFiles);
      
      // STEP 2: Update database (real-time listener will now match local state)
      await CloudFileService.updateFileOrders(caseId, fileOrderUpdates);

      // STEP 3: Update case lastModified since files were reordered
      try {
        const caseRef = doc(db, 'cases', caseId);
        await updateDoc(caseRef, {
          lastModified: serverTimestamp()
        });
        console.log('Case lastModified updated after reorder');
        onCaseModified?.();
      } catch (caseUpdateError) {
        // Log but don't fail the whole operation
        console.warn('Failed to update case lastModified after reorder:', caseUpdateError);
      }
      
      console.log('✅ EvidenceManager - Files reordered successfully');
      showStatus('Files reordered successfully.');
      trackFileReordered(caseId, fileOrderUpdates.length);
      
    } catch (error) {
      console.error('EvidenceManager - Error reordering files:', error);
      showStatus('Error reordering files', error);
      
      // Reload files to restore correct state
      await loadFiles();
    }
  }, [currentUser?.uid, caseId, showStatus, loadFiles, pdfFiles, setPdfFiles]);
  
  // Export combined PDF - Enhanced for optimized workflow
  const handleExportCombinedPdf = useCallback(async () => {
    if (pdfFiles.length === 0) {
      showStatus('No files to export.');
      return;
    }

    // CHECK: Use the optimized processing summary
    if (!processingSummary.canExport) {
      if (processingSummary.processing > 0) {
        showStatus(`Cannot export: ${processingSummary.processing} document${processingSummary.processing !== 1 ? 's are' : ' is'} still being processed.`);
      } else if (processingSummary.failed > 0) {
        showStatus(`Cannot export: ${processingSummary.failed} document${processingSummary.failed !== 1 ? 's' : ''} failed to process.`);
      } else {
        showStatus('Cannot export: Not all documents are ready yet.');
      }
      return;
    }
    
    // This check is now redundant - processingSummary.canExport already validates
    // that all files have status 'completed' and are ready to export.
    // The subsequent searchablePdfUrl check at line 1091 ensures all files have URLs.
    
    if (!caseData || !stampConfig) {
      showStatus('Case information or stamp configuration not loaded. Please refresh the page and try again.');
      return;
    }
    
    try {

      setIsExporting(true);
      showStatus('Preparing to export combined PDF...');

      // STRICT VALIDATION: All files must be searchable for export
      const filesWithoutSearchablePdf = pdfFiles.filter(file => 
        !file.searchablePdfUrl
      );

      if (filesWithoutSearchablePdf.length > 0) {
        const fileNames = filesWithoutSearchablePdf.map(f => f.name || f.originalName).join(', ');
        throw new Error(`Export failed: ${filesWithoutSearchablePdf.length} file(s) are not searchable: ${fileNames}. Please wait for OCR processing to complete.`);
      }

      setIsExporting(true);
      const startTime = Date.now();
      
      trackExportStarted(pdfFiles.length, caseId);

      console.log('EvidenceManager - Starting export with stamp config:', stampConfig);
      console.log('EvidenceManager - Case data:', caseData);
      
      // NEW: Determine what we're exporting and get the set info
      let evidenceWithDisplayData;
      let documentSetInfo = null;
      
      if (selectedSetId) {
        // EXPORTING A DOCUMENT SET
        const setDocuments = pdfFiles.filter(file => {
          if (file.isIndex) return false;
          return (file.documentSets || []).includes(selectedSetId);
        });
        
        // Get the set name for the index
        const selectedSet = documentSets.find(s => s.id === selectedSetId);
        documentSetInfo = selectedSet ? {
          name: selectedSet.name,
          color: selectedSet.color
        } : null;
        
        // Use inherited numbers but filtered list
        evidenceWithDisplayData = EvidenceDataService.getFilteredDisplayDataWithInheritedNumbers(
          pdfFiles,      // Full array for inherited numbers
          setDocuments,  // Filtered set for export
          stampConfig,
          caseData
        );
        
        console.log(`Exporting document set "${selectedSet?.name}" with ${evidenceWithDisplayData.length} documents`);
      } else {
        // EXPORTING FULL CASE
        evidenceWithDisplayData = EvidenceDataService.getDocumentDisplayData(
          pdfFiles,
          stampConfig,
          caseData
        );
        
        console.log(`Exporting full case with ${evidenceWithDisplayData.length} documents`);
      }

      // Use the searchable PDF URLs for export and include stamp positions
      const evidenceForExport = evidenceWithDisplayData.map(file => ({
        ...file,
        file: {
          url: file.searchablePdfUrl,
          name: file.name || file.originalName
        }
      }));

      // Pass documentSetInfo to export
      await exportCombinedPdf(
        evidenceForExport, 
        stampConfig, 
        caseData, 
        (status) => showStatus(status),
        documentSetInfo  // NEW PARAMETER
      );

      const duration = Date.now() - startTime;
      trackExportCompleted(evidenceForExport.length, duration, true);
      trackPerformance('pdf_export', duration);

      showStatus('Export completed successfully!', 'success');
      if (currentUser?.uid) {
        await incrementActionCount(currentUser.uid, 'exportDocs');
      }

      // Show download complete modal
      const selectedSet = selectedSetId
        ? documentSets.find(s => s.id === selectedSetId)
        : null;

      // Extract document IDs from the exported evidence
      const documentIds = evidenceForExport.map(file => file.id);

      setDownloadModalData({
        caseName: caseData.name,
        documentSet: selectedSet,
        allDocumentIds: documentIds
      });
      setIsDownloadModalOpen(true);

    } catch (error) {
      console.error('EvidenceManager - Export error:', error);
      trackExportFailed(pdfFiles.length, error.message);
      showStatus(`Export failed: ${error.message}`, error);
    } finally {
      setIsExporting(false);
    }
  }, [pdfFiles, processingSummary, stampConfig, caseData, showStatus, selectedSetId, documentSets]);
  /**
   * Create a new document set with specific color
   */
  const handleCreateDocumentSet = useCallback(async (name, color) => {
    if (!caseId) return;

    try {
      // Pass color to service so preview matches final color
      const newSet = await DocumentSetService.createDocumentSet(caseId, name, color);

      // Update CaseManager's state instead of local state
      if (onDocumentSetsChange) {
        onDocumentSetsChange(prev => prev.map(c => {
          if (c.id === caseId) {
            return {
              ...c,
              documentSets: [...(c.documentSets || []), newSet]
            };
          }
          return c;
        }));
      }

      console.log('Created document set:', newSet);
      if (currentUser?.uid) {
        await incrementActionCount(currentUser.uid, 'createTag');
        await incrementActionCount(currentUser.uid, 'createTagInMainFlow');
      }
      return newSet; // IMPORTANT: Return the new set so selector can auto-select it
    } catch (error) {
      console.error('Error creating document set:', error);
      showStatus(`Error creating set: ${error.message}`, error);
    }
  }, [caseId, showStatus, onDocumentSetsChange]);

  /**
   * Create a new document set and link it with specific documents
   * Used by the download complete modal to create tags for downloaded docs
   */
  const handleCreateDocumentSetWithDocs = useCallback(async (name, color, documentIds) => {
    if (!caseId) return;

    try {
      console.log('EvidenceManager: Creating tag and linking docs', { name, color, documentIds });

      // First, create the document set
      const newSet = await DocumentSetService.createDocumentSet(caseId, name, color);
      console.log('EvidenceManager: Tag created', newSet);
      console.log('EvidenceManager: Tag created', newSet);
      await incrementActionCount(currentUser.uid, 'createTag');
      await incrementActionCount(currentUser.uid, 'createTagFromExportModal');

      // Then, link all documents to this set by appending to their existing tags
      const linkPromises = documentIds.map(docId => {
        // Find the document and get its current set IDs
        const doc = pdfFiles.find(f => f.id === docId);
        const currentSetIds = doc?.documentSets || [];

        // Append the new set ID to existing ones (avoid duplicates)
        const updatedSetIds = currentSetIds.includes(newSet.id)
          ? currentSetIds
          : [...currentSetIds, newSet.id];

        console.log(`EvidenceManager: Updating doc ${docId} tags from [${currentSetIds}] to [${updatedSetIds}]`);
        return DocumentSetService.updateDocumentSets(caseId, docId, updatedSetIds);
      });
      await Promise.all(linkPromises);
      console.log('EvidenceManager: Documents linked to tag');

      // Update CaseManager's state
      if (onDocumentSetsChange) {
        onDocumentSetsChange(prev => prev.map(c => {
          if (c.id === caseId) {
            return {
              ...c,
              documentSets: [...(c.documentSets || []), newSet]
            };
          }
          return c;
        }));
      }

      return newSet;
    } catch (error) {
      console.error('Error creating document set with docs:', error);
      showStatus(`Error creating set: ${error.message}`, error);
      throw error;
    }
  }, [caseId, showStatus, onDocumentSetsChange, pdfFiles]);

  /**
   * Update the complete set of sets for a document
   * Replaces the old add/remove pattern with a single operation
   */
  const handleUpdateDocumentSets = useCallback(async (fileId, setIds) => {
    if (!caseId) return;

    try {
      const currentFile = pdfFiles.find(f => f.id === fileId);
      const currentSetIds = currentFile?.documentSets || [];
      const isAddingTags = setIds.some(id => !currentSetIds.includes(id));

      await DocumentSetService.updateDocumentSets(caseId, fileId, setIds);
      console.log(`Updated document ${fileId} sets to:`, setIds);

      if (isAddingTags && currentUser?.uid) {
        await incrementActionCount(currentUser.uid, 'addTagToSingleDoc');
      }

      // No optimistic update - let real-time listener handle it
      // This prevents race conditions and ensures UI matches reality
    } catch (error) {
      console.error('Error updating document sets:', error);
      showStatus(`Error updating sets: ${error.message}`, error);
    }
  }, [caseId, pdfFiles, currentUser?.uid, showStatus]);

  /**
   * Clear all sets from a document
   */
  const handleClearAllSetsFromDocument = useCallback(async (fileId) => {
    if (!caseId) return;

    try {
      await DocumentSetService.clearAllSetsFromDocument(caseId, fileId);
      console.log(`Cleared all sets from document ${fileId}`);
      
      // Update local file state
      setPdfFiles(prev =>
        prev.map(file =>
          file.id === fileId
            ? {
                ...file,
                documentSets: [],
                documentSetOrder: []
              }
            : file
        )
      );
    } catch (error) {
      console.error('Error clearing sets from document:', error);
      showStatus(`Error clearing sets: ${error.message}`, error);
    }
  }, [caseId, showStatus]);

  /**
   * Reorder ALL document sets at case level (for popover list)
   */
  const handleReorderAllDocumentSets = useCallback(async (newOrderedSets) => {
    if (!caseId) return;

    try {
      await DocumentSetService.reorderAllDocumentSets(caseId, newOrderedSets);
      console.log('Reordered all document sets:', newOrderedSets);

      // Update CaseManager's state instead of local state
      if (onDocumentSetsChange) {
        onDocumentSetsChange(prev => prev.map(c => {
          if (c.id === caseId) {
            return {
              ...c,
              documentSets: newOrderedSets
            };
          }
          return c;
        }));
      }
    } catch (error) {
      console.error('Error reordering document sets:', error);
      showStatus(`Error reordering sets: ${error.message}`, error);
    }
  }, [caseId, showStatus, onDocumentSetsChange]);

  /**
   * Batch add sets to multiple documents
   * Uses OPTIMISTIC UPDATE for instant UI + atomic batch operation for database
   */
  const handleBatchAddSetsToDocuments = useCallback(async (fileIds, setIds) => {
    if (!caseId) return;

    // Store previous state for rollback on error
    const previousFiles = pdfFiles;
    const startTime = performance.now();

    try {
      console.log(`Batch adding ${setIds.length} sets to ${fileIds.length} documents`);

      // OPTIMISTIC UPDATE: Update UI immediately
      setPdfFiles(currentFiles =>
        currentFiles.map(file => {
          if (!fileIds.includes(file.id)) return file;

          const currentSets = file.documentSets || [];
          const currentOrder = file.documentSetOrder || currentSets;

          // Calculate union: existing sets + new sets (no duplicates)
          const newSetsToAdd = setIds.filter(setId => !currentSets.includes(setId));
          if (newSetsToAdd.length === 0) return file;

          return {
            ...file,
            documentSets: [...currentSets, ...newSetsToAdd],
            documentSetOrder: [...newSetsToAdd, ...currentOrder],
          };
        })
      );

      // Then persist to database (real-time listener will confirm)
      await DocumentSetService.batchAddSetsToDocuments(caseId, fileIds, setIds);

      // Track successful bulk operation with performance metrics
      const durationMs = Math.round(performance.now() - startTime);
      trackBulkSetsAddedToDocuments(fileIds.length, setIds.length, 0, durationMs);
      if (currentUser?.uid) {
        await incrementActionCount(currentUser.uid, 'bulkTag');
      }

    } catch (error) {
      console.error('Error batch adding sets:', error);
      showStatus(`Error adding sets: ${error.message}`, error);

      // ROLLBACK: Restore previous state on error
      setPdfFiles(previousFiles);
    }
  }, [caseId, showStatus, pdfFiles, currentUser?.uid]);

  /**
   * Batch remove sets from multiple documents
   * Uses OPTIMISTIC UPDATE for instant UI + atomic batch operation for database
   */
  const handleBatchRemoveSetsFromDocuments = useCallback(async (fileIds, setIds) => {
    if (!caseId) return;

    // Store previous state for rollback on error
    const previousFiles = pdfFiles;
    const startTime = performance.now();

    try {
      console.log(`Batch removing ${setIds.length} sets from ${fileIds.length} documents`);

      // OPTIMISTIC UPDATE: Update UI immediately
      setPdfFiles(currentFiles =>
        currentFiles.map(file => {
          if (!fileIds.includes(file.id)) return file;

          const currentSets = file.documentSets || [];
          const currentOrder = file.documentSetOrder || currentSets;

          // Calculate difference: remove specified sets
          const finalSets = currentSets.filter(id => !setIds.includes(id));
          const finalOrder = currentOrder.filter(id => !setIds.includes(id));

          // Only update if something changed
          if (finalSets.length === currentSets.length) return file;

          return {
            ...file,
            documentSets: finalSets,
            documentSetOrder: finalOrder,
          };
        })
      );

      // Then persist to database (real-time listener will confirm)
      await DocumentSetService.batchRemoveSetsFromDocuments(caseId, fileIds, setIds);

      // Track successful bulk operation with performance metrics
      const durationMs = Math.round(performance.now() - startTime);
      trackBulkSetsRemovedFromDocuments(fileIds.length, setIds.length, durationMs);
      if (currentUser?.uid) {
        await incrementActionCount(currentUser.uid, 'bulkRemoveTag');
      }

    } catch (error) {
      console.error('Error batch removing sets:', error);
      showStatus(`Error removing sets: ${error.message}`, error);

      // ROLLBACK: Restore previous state on error
      setPdfFiles(previousFiles);
    }
  }, [caseId, showStatus, pdfFiles]);

  // Debug: Log selectedFile structure when it changes
  useEffect(() => {
    if (selectedFile) {
      trackFilePreviewOpened(selectedFile.id, selectedFile.fileType)
    }
  }, [selectedFile]);
  
  // Real-time listener with debouncing to prevent thundering herd
  useEffect(() => {
    if (!caseId) return;

    console.log(`👂 Setting up real-time status updates for case: ${caseId}`);

    // Track if this effect is still active (not cleaned up)
    let isActive = true;

    // Create query for files in this case
    const filesRef = collection(db, 'cases', caseId, 'files');

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      filesRef,
      async (snapshot) => {
        // CRITICAL FIX: Check if we're still active before processing
        if (!isActive) {
          console.log(`🚫 Ignoring stale update for case ${caseId} (component switched cases)`);
          return;
        }

        // Use docChanges() to only process what actually changed
        const changes = snapshot.docChanges();
        console.log(`📡 File changes received for case ${caseId}: ${changes.length} changes (total docs: ${snapshot.size})`);

        // Separate changes by type
        const toProcess = []; // added or modified
        const toRemove = [];  // removed

        changes.forEach(change => {
          if (change.type === 'removed') {
            toRemove.push(change.doc.id);
          } else {
            // 'added' or 'modified'
            toProcess.push({ id: change.doc.id, data: change.doc.data() });
          }
        });

        console.log(`📊 Processing ${toProcess.length} adds/modifications, ${toRemove.length} removals`);

        // Process adds and modifications (need URL generation)
        if (toProcess.length > 0) {
          const processedFiles = await Promise.all(
            toProcess.map(async ({ id, data }) => {
              // Check if still active before each async operation
              if (!isActive) return null;

              const fileData = { id, ...data };

              // Only generate URL for searchable PDF if path exists
              if (data.searchablePdfPath) {
                try {
                  const searchableUrl = await CloudFileService.getAuthenticatedUrl(data.searchablePdfPath);
                  if (searchableUrl && isActive) {
                    fileData.searchablePdfUrl = searchableUrl;
                  }
                } catch (error) {
                  console.warn(`Could not get auth URL for searchable PDF ${data.searchablePdfPath}:`, error);
                  // File will show error state in UI
                }
              }

              return fileData;
            })
          );

          // Final check before updating state
          if (!isActive) {
            console.log(`🚫 Discarding results for case ${caseId} (component switched cases)`);
            return;
          }

          // Filter out nulls (from cancelled operations)
          const validFiles = processedFiles.filter(f => f !== null);

          console.log(`✅ Processed ${validFiles.length} files`);

          // Queue for debounced processing
          queueRealtimeUpdate(validFiles, toRemove);
        } else if (toRemove.length > 0) {
          // Only removals, no adds/modifications
          queueRealtimeUpdate([], toRemove);
        }

        setIsLoading(false);
      },
      (error) => {
        console.error(`❌ Error listening to file updates for case ${caseId}:`, error);
      }
    );

    // Cleanup on unmount
    return () => {
      console.log(`🔇 Cleaning up real-time listener for case: ${caseId}`);
      // Mark as inactive to cancel in-flight async operations
      isActive = false;
      // Clear any pending debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      unsubscribe();
    };
  }, [caseId, queueRealtimeUpdate]);


  // Auto-select first non-index document when files load or selection becomes invalid
  useEffect(() => {
    // Only auto-select if no PDF is currently selected OR if selected PDF no longer exists
    const selectedExists = displayFiles.some(f => f.id === selectedPdfId);
    
    if (!selectedPdfId || !selectedExists) {
      // Find first non-index document
      const firstRealDoc = displayFiles.find(f => f.id !== 'INDEX_DOCUMENT');
      
      if (firstRealDoc) {
        console.log('📄 Auto-selecting first document:', firstRealDoc.id);
        setSelectedPdfId(firstRealDoc.id);
      }
    }
  }, [displayFiles, selectedPdfId]);

  // Initialize stamp positions when files load
  useEffect(() => {
    console.log('🎨 Stamp positions useEffect running');
    
    // Collect all new positions first
    const newPositions = [];
    
    pdfFiles.forEach(file => {
      // Skip if we already have this position
      if (stampPositions.has(file.id)) {
        return;
      }
      
      // Skip if file doesn't have stamp data yet
      if (!file.stampLocation || !file.pdfDimensions) {
        return;
      }
      
      // Calculate and store new position
      const percentPosition = pdfPointsToPercentages(
        file.stampLocation,
        file.pdfDimensions.width,
        file.pdfDimensions.height
      );
      
      newPositions.push({ fileId: file.id, position: percentPosition });
    });
    
    // Only update state if we have new positions to add
    if (newPositions.length > 0) {
      console.log('🎨 Adding', newPositions.length, 'stamp positions');
      setStampPositions(prev => {
        const newMap = new Map(prev);
        newPositions.forEach(({ fileId, position }) => {
          newMap.set(fileId, position);
        });
        return newMap;
      });
    } else {
      console.log('🎨 No new stamp positions to add');
    }
  }, [pdfFiles]);

  // Communicate export state changes to parent
  useEffect(() => {
    if (onExportStateChange) {
      onExportStateChange({
        isExporting,
        canExport: processingSummary.canExport,
        hasErrors: processingSummary.hasErrors,
      });
    }
  }, [isExporting, processingSummary.canExport, processingSummary.hasErrors, onExportStateChange]);

  // Connect export function to parent trigger ref
  useEffect(() => {
    if (exportTriggerRef) {
      exportTriggerRef.current = handleExportCombinedPdf;
    }
  }, [exportTriggerRef, handleExportCombinedPdf]);

  // Archive handlers
  const handleArchiveSet = useCallback(async (setId, shouldArchive) => {
    if (!caseId || !setId) return;

    try {
      const newArchivedState = await DocumentSetService.toggleArchiveDocumentSet(caseId, setId);

      if (currentUser?.uid) {
        await incrementActionCount(currentUser.uid, newArchivedState ? 'archiveTag' : 'restoreTag');
      }

      // Update local state via CaseManager
      if (onDocumentSetsChange) {
        onDocumentSetsChange(prev => prev.map(c => {
          if (c.id === caseId) {
            return {
              ...c,
              documentSets: (c.documentSets || []).map(s =>
                s.id === setId ? { ...s, archived: newArchivedState } : s
              )
            };
          }
          return c;
        }));
      }

      console.log(`Document set ${setId} ${newArchivedState ? 'archived' : 'unarchived'}`);
    } catch (error) {
      console.error('Error archiving document set:', error);
      showStatus(`Error archiving set: ${error.message}`, error);
    }
  }, [caseId, onDocumentSetsChange, showStatus]);

  const handleArchiveCaseFromModal = useCallback(async (shouldArchive) => {
    if (!caseId) return;

    try {
      // Call the CaseManager's archive handler
      if (onArchiveCase) {
        await onArchiveCase(caseId);
        console.log(`Case ${caseId} ${shouldArchive ? 'archived' : 'unarchived'}`);
      }
    } catch (error) {
      console.error('Error archiving case:', error);
      showStatus(`Error archiving case: ${error.message}`, error);
    }
  }, [caseId, onArchiveCase, showStatus]);

  return (
    <ErrorBoundary>
      <div className="h-full w-full">
        <ScrollContainer 
          direction="horizontal"
          className="h-full w-full bg-app-gray-white"
          alwaysVisible={true}
        >
          <div
            data-guide-layout
            className="h-full bg-app-gray-white"
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(400px, 2fr) minmax(300px, 3fr)',
              height: '100%',
              minWidth: '700px'
            }}
          >
            {/* Left Column - File Management */}
            <div 
              className="flex flex-col px-app-3xl pb-app-2xl pt-app-xl h-full min-w-0"
              style={{ height: '100%', overflow: 'hidden' }}
            >  
              {/* Header Section */}
              <header className="flex items-center gap-app-xl pb-app-lg h-[48px]">
                <h2 className="text-app-xl font-medium text-app-gray-900">Documents</h2>
                <div className="flex-1 max-w-[196px] min-w-0 ml-auto">
                  <SearchInput
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    placeholder="Search..."
                  />
                </div>
              </header>
              
              {/* File List */}
              <div className="flex-1 h-0">
                <PdfTable
                  pdfFiles={documentsWithDisplayData}
                  allPdfFiles={displayFiles}
                  isFiltered={isFiltered}
                  selectedPdfId={selectedPdfId}
                  onSelectPdf={handleSelectPdf}
                  onRemovePdf={onRemovePdf}
                  onReorderPdfs={handleReorderPdfs}
                  onUpdateFileName={handleUpdateFileName}
                  stampConfig={stampConfig}
                  caseData={caseData}
                  onFilesAdded={addFiles}
                  isUploading={isUploading}
                  uploadProgress={uploadProgress}
                  hasSearchTerm={debouncedSearchTerm.trim() !== ''}
                  allDocumentsCount={displayFiles.length}
                  isLoading={isLoading}
                  documentSets={documentSets}
                  onCreateDocumentSet={handleCreateDocumentSet}
                  onUpdateDocumentSets={handleUpdateDocumentSets}
                  onClearAllSetsFromDocument={handleClearAllSetsFromDocument}
                  onReorderDocumentSets={handleReorderAllDocumentSets}
                  onBatchAddSetsToDocuments={handleBatchAddSetsToDocuments}
                  onBatchRemoveSetsFromDocuments={handleBatchRemoveSetsFromDocuments}
                  selectedSetId={selectedSetId}
                  isSetsCollapsed={isSetsCollapsed}
                  onSetsCollapsedChange={onSetsCollapsedChange}
                  onSelectionChange={setSelectedDocCount}
                  onStartGuide={handleStartBulkGuide}
                  onStartTagGuide={onStartTagGuide}
                  filterErrors={filterErrors}
                />
              </div>
            </div>
            
            {/* Right Column - PDF Viewer */}
            <div 
              className="flex flex-col pr-app-3xl pb-app-2xl pt-app-xl h-full min-w-0"
              style={{ height: '100%', overflow: 'hidden' }}
            >
              
              {/* Viewer Title Bar */}
              <header className="flex items-center pb-app-lg h-[48px]">
                <h2 className="text-app-xl font-medium text-app-gray-900 truncate mr-app-md">
                  {selectedFile ? (selectedFile.name || 'Document') : 'Viewer'}
                </h2>
                
                <IconButton
                  className="flex-shrink-0"
                  onClick={handleDownloadPdf}
                  disabled={!selectedFile}
                  title="Download PDF with stamp"
                  size="lg"
                >
                  <FileDown strokeWidth={2} />
                </IconButton>
              </header>

              {/* Viewer Content */}
              <div className="flex-1 min-h-0 bg-graph-paper rounded-app-lg overflow-hidden">
                <ScrollContainer
                  className="h-full w-full"
                  trackMargins={{ top: 16, bottom: 16 }}
                >
                  {isLoading ? (
                    // Show skeleton while loading documents
                    <PdfSkeleton documentName="Loading..." />
                  ) : selectedFile && (selectedFile.status === 'ocr_failed' || selectedFile.status === 'error' || selectedFile.status === 'client_error') ? (
                    // Show error state for failed documents
                    <ViewerEmptyState
                      hasDocuments={pdfFiles.length > 0}
                      isError={true}
                      errorType={selectedFile.errorType || null}
                      errorSubtype={selectedFile.errorSubtype || null}
                      fileInfo={{
                        name: selectedFile?.originalName || selectedFile?.name,
                        id: selectedFile?.id,
                        caseId: selectedFile?.caseId,
                      }}
                      onReplace={(newFile) => onReplaceFile(selectedFile.id, newFile)}
                      onDelete={() => onRemovePdf(selectedFile.id)}
                    />
                  ) : selectedFile ? (
                    // Show normal PDF viewer for successfully processed documents
                    <PdfViewer
                      key={selectedFile.id}
                      pdfFile={selectedFile}
                      stampConfig={stampConfig}
                      caseData={caseData}
                      documentIndex={selectedFile.documentIndex}
                      startPage={selectedFile.startPage}
                      showStatus={showStatus}
                      initialStampPosition={getCurrentStampPosition(selectedFile)}
                      onStampLocationUpdate={(stampLocationPercent) => {
                        handleStampPositionUpdate(selectedFile.id, stampLocationPercent);
                      }}
                    />
                  ) : (
                    // Show normal empty state when no document is selected
                    <ViewerEmptyState hasDocuments={pdfFiles.length > 0} />
                  )}
                </ScrollContainer>
              </div>
            </div>
          </div>
        </ScrollContainer>
      </div>

      <BulkTagGuide
        visible={bulkTagVisible || forceBulkGuide}
        step={bulkTagStep}
        onNext={handleNextStep}
        onGotIt={handleBulkTagGotIt}
        onClose={handleBulkTagClose}
        onStepChange={handleStepChange}
      />

      {/* Download Complete Modal */}
      <DownloadCompleteModal
        isOpen={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
        caseName={downloadModalData.caseName}
        documentSet={downloadModalData.documentSet}
        isDownloading={false}
        onArchiveSet={handleArchiveSet}
        onArchiveCase={handleArchiveCaseFromModal}
        documentSets={documentSets}
        onCreateDocumentSet={handleCreateDocumentSetWithDocs}
        allDocumentIds={downloadModalData.allDocumentIds}
      />

    </ErrorBoundary>
  );
}

export default EvidenceManager;